<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { BUILD_STAMP } from '@/lib/buildStamp'
import { WHITE, paintIndexOf } from '@/lib/paint'

/**
 * The car's paint: the bodywork's colour, one colour per overlay slot the chosen livery draws, and
 * the livery itself — which is what decides how many of those colours there are.
 *
 * The preview is drawn from the GAME's own files — the car's config (its sprite box, its layers and
 * its livery catalogue) and its sprites, both brought over by `sync:assets` — so what is shown here
 * is what the game builds, out of the same numbers and the same artwork, not a second drawing of it
 * that could drift.
 *
 * Which colours and liveries can be picked at all is not for this component to decide: they arrive
 * as props — for a player, what the account's tier allows — and everything outside those lists is
 * hidden rather than refused, a choice nobody can make not being a choice. The server refuses them
 * too: what a tier allows is its answer to give (see App\Libraries\AppearanceLib).
 */
const props = defineProps({
  /** { color, color2, color3, color4, livery } — the values to edit, and what the preview draws. */
  modelValue: { type: Object, required: true },
  /** The car the preview is drawn on: chosen by the page, the same one for everybody. */
  car:        { type: String, default: '' },
  /** The cars the site offers (config/cars.php, from the bootstrap): the first is the fallback. */
  cars:       { type: Array,  default: () => [] },
  /** The colours the pickers offer: what this account may pick, a slice of config/colors.php. */
  colors:     { type: Array,  default: () => [] },
  /** Whether the account may pick any colour at all (color.rgb), not only one of `colors`. */
  anyColor:   { type: Boolean, default: false },
  /** The livery ids the account may wear (config/appearance.php): the rest is hidden. */
  liveries:   { type: Array,  default: () => [] },
  disabled:   { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'palette'])

const { t, te } = useI18n()

const ZOOM       = 3

const canvas = ref(null)
const openMenu = ref(null);   // which colour's palette is open, so one can be closed from code
const carDef = ref(null)
const shownCar = ref('');   // the id actually drawn (the one the page handed over), the files' source
const failed = ref(false)

/** The ids of this car's liveries the account may wear: the rest is neither listed nor fetched. */
function wornLiveries(def) {
  return Object.keys(def?.liveries ?? {}).filter((id) => props.liveries.includes(id))
}

const liveryItems = computed(() => {
  const items = [{ value: '', title: t('settings.game.liveryPlain') }]
  for (const id of wornLiveries(carDef.value)) {
    const livery = carDef.value.liveries[id]
    items.push({ value: id, title: te(`liveries.${id}`) ? t(`liveries.${id}`) : (livery.name ?? id) })
  }
  return items
})

function update(patch) {
  emit('update:modelValue', { ...props.modelValue, ...patch })
}

/** The browser's own picker reports the colour on `change`; the palette has done its job by then. */
function pickFree(key, event) {
  update({ [key]: event.target.value })
  openMenu.value = null
}

// --- the preview ----------------------------------------------------------------------------------

// One promise per file: a sprite is never fetched twice, and one that is not there draws nothing
// (which is what the game does too) rather than blocking the rest.
const images = new Map()
function image(src) {
  if (!images.has(src)) {
    images.set(src, new Promise((resolve) => {
      const img = new Image()
      img.onload  = () => resolve(img)
      img.onerror = () => resolve(null)
      img.src = src
    }))
  }
  return images.get(src)
}

/**
 * A layer with a colour multiplied over it, keeping its own shading and its own outline: draw the
 * artwork, multiply the colour in, then keep only where the artwork is. This is the canvas form of
 * what the game does with a tint (see CarRenderer._applyLivery).
 * Kept per image and colour: a colour change redraws the same few images, and re-running the
 * multiply for each of them on every change is work nobody asked for.
 */
const tintedLayers = new Map()
function tinted(img, colour) {
  const key  = `${img.src}|${colour}`
  const made = tintedLayers.get(key)
  if (made) return made

  const out = document.createElement('canvas')
  out.width  = img.naturalWidth
  out.height = img.naturalHeight
  const x = out.getContext('2d')
  x.drawImage(img, 0, 0)
  x.globalCompositeOperation = 'multiply'
  x.fillStyle = colour
  x.fillRect(0, 0, out.width, out.height)
  x.globalCompositeOperation = 'destination-in'
  x.drawImage(img, 0, 0)
  tintedLayers.set(key, out)
  return out
}

const WHEEL = 'wheel'

/**
 * What one car draws, in draw order — the same expansion the game does (CarRenderer._carLayers):
 * the `livery` marker becomes the chosen livery's own files, `wheel` is not a canvas but the tyres'
 * place in the order (the game inserts it just under the bodywork), and a car that lists no layers
 * at all is its one flat `<id>.png`.
 */
function drawOrder(def, livery) {
  const asked = def.layers
  if (!asked?.length) return [null, ...(def.wheels ? [WHEEL] : [])]
  const out = []
  for (const name of asked) {
    if (name === 'livery')   out.push(...(def.liveries?.[livery]?.layers ?? []))
    else if (name === WHEEL) continue;   // reserved: the tyres come from the car's wheels block
    else {
      if (name === 'body' && def.wheels) out.push(WHEEL);
      out.push(name)
    }
  }
  return out
}

// A layer's name is its file suffix; `null` is the flat <id>.png a car with no layers has.
// Stamped like the catalogue above: a sprite redrawn in place has the same name in the next deploy.
const file = (name) => image(`/img/cars/${shownCar.value}${name ? `_${name}` : ''}.png?v=${BUILD_STAMP}`)

/** The slot of an overlay file (`layer_<slot>_<design>`), or null for anything else. */
const slotOf = (name) => /^layer_(\d+)_/.exec(name ?? '')?.[1] ?? null

/**
 * The colours on offer: the bodywork always, then one per overlay slot the CURRENT paint draws. The
 * slots are the livery's — a one-slot livery asks for one overlay colour and nothing else — so what
 * is shown is what the car really paints, and a colour nothing paints is not a choice.
 *
 * The keys are the ones the game reads (`slotOf` + 1: slot 1 → color2), so what is stored is exactly
 * what CarRenderer tints with — and a colour left over from another livery comes back whole as soon
 * as a livery using its slot is picked again (the rows bound to a value do not erase it, see
 * `update`).
 */
const paintKeys = computed(() => {
  if (!carDef.value) return ['color']

  const slots = new Set()
  for (const name of drawOrder(carDef.value, props.modelValue.livery)) {
    const slot = slotOf(name)
    if (slot) slots.add(Number(slot))
  }

  return ['color', ...[...slots].sort((a, b) => a - b).map((slot) => `color${slot + 1}`)]
})

/**
 * The colours this paint offers, by index (1 = bodywork), which is what a crest is drawn with: null
 * until the car's file is in, so a reader never mistakes the plain car's one colour for the answer.
 */
watch([paintKeys, failed], () => {
  emit('palette', carDef.value ? paintKeys.value.map(paintIndexOf) : (failed.value ? [1] : null))
}, { immediate: true })

/**
 * Every sprite this car can be drawn with: its own layers, everything every one of its liveries
 * adds, and the tyre artwork. All of them are fetched (and decoded) when the section is opened, so
 * switching a livery or a colour afterwards has nothing left to wait for — the first draw of a
 * livery used to sit there while its two PNGs came in.
 */
function previewFiles(def) {
  const orders = [drawOrder(def, ''), ...wornLiveries(def).map((livery) => drawOrder(def, livery))]
  const wanted = new Set()
  for (const order of orders) {
    for (const name of order) {
      if (name === WHEEL) for (const art of (def.wheels?.art ?? [WHEEL]).slice(0, 2)) wanted.add(art)
      else wanted.add(name)
    }
  }
  return [...wanted]
}

async function draw() {
  const def = carDef.value
  const el  = canvas.value
  if (!def || !el) return

  el.width  = Math.max(1, Math.round(def.width * ZOOM))
  el.height = Math.max(1, Math.round(def.height * ZOOM))
  const x = el.getContext('2d')
  x.clearRect(0, 0, el.width, el.height)
  if (failed.value) return

  const paint = props.modelValue

  // The wheels, at the car's own anchors: one sprite per wheel, the sides from their own artwork
  // when the car names two (the canvas' top edge is a car drawn nose-right's left side, so side -1
  // takes the first file).
  const tyres = async () => {
    const w = def.wheels
    if (!w || ![w.frontX, w.rearX, w.halfTrack, w.length, w.width].every(Number.isFinite)) return
    const names = (w.art ?? [WHEEL]).slice(0, 2)
    const art   = await Promise.all([file(names[0]), file(names[1] ?? names[0])])
    for (const [side, index] of [[-1, 0], [1, 1]]) {
      for (const ax of [w.frontX, w.rearX]) {
        const img = art[index]
        if (!img) continue
        x.drawImage(img,
          (def.width  / 2 + ax - w.length / 2) * ZOOM,
          (def.height / 2 + side * w.halfTrack - w.width / 2) * ZOOM,
          w.length * ZOOM, w.width * ZOOM)
      }
    }
  }

  for (const name of drawOrder(def, paint.livery)) {
    if (name === WHEEL) { await tyres(); continue }
    const img = await file(name)
    if (!img) continue
    // colour 1 paints the bodywork (and a car with no layers is all bodywork), colour <slot>+1 the
    // file of overlay slot <slot> — white when the account has chosen no such colour, as in game
    const slot   = slotOf(name)
    const colour = (name === 'body' || name === null) ? (paint.color ?? null)
                 : slot ? (paint[`color${Number(slot) + 1}`] ?? WHITE) : null
    x.drawImage(colour ? tinted(img, colour) : img, 0, 0, el.width, el.height)
  }
}

// The car's own file is what the preview is built from, and it is the game's file — so a car whose
// sprites are missing somewhere shows it here before it shows in game. One fetch per car, kept.
// The URL carries the build's stamp: the file keeps its name across deploys, and without it the
// browser would answer with the catalogue it downloaded before the liveries were added.
const defs = new Map()
function carFile(carId) {
  if (!defs.has(carId)) {
    defs.set(carId, fetch(`/cars/${carId}.json?v=${BUILD_STAMP}`)
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null))
  }
  return defs.get(carId)
}

// The car the page hands over, with the first offered one as the fallback: which car the paint is
// shown on is the page's decision (config/cars.php marks it, one for everybody and always the same,
// so two players compare the same car) and not something to search for here — the caption under the
// canvas says which car it is.
watch([() => props.car, () => props.cars], async ([car, cars]) => {
  carDef.value = null
  failed.value = false
  const carId = car || cars[0] || ''
  const def   = carId ? await carFile(carId) : null
  if (!def) {
    if (carId) failed.value = true
    return
  }
  shownCar.value = carId
  carDef.value   = def
  await nextTick()
  const firstPaint = draw()      // with whatever is already decoded: the panel is never blank
  preload(def)
  await firstPaint
}, { immediate: true })

// Fetches everything the preview can draw on this car (see previewFiles), after the first paint has
// gone out: what is on screen never waits for what is only needed later.
async function preload(def) {
  await Promise.all(previewFiles(def).map((name) => file(name)))
}

watch(() => props.modelValue, () => draw(), { deep: true })
</script>

<template>
  <div>
    <v-alert v-if="failed" type="warning" density="compact" variant="tonal" class="mb-4">
      {{ t('settings.game.previewError') }}
    </v-alert>

    <div v-if="shownCar" class="car-preview mb-5">
      <canvas ref="canvas" aria-hidden="true"></canvas>
      <!-- the car's own name, so it is clear which car is being painted -->
      <!-- <div class="text-body-2 text-medium-emphasis">{{ carDef?.name ?? shownCar }}</div> -->
    </div>

    <div class="color-grid">
      <div v-for="key in paintKeys" :key="key" class="d-flex align-center">
        <div class="text-subtitle-2 color-picker-label">{{ t(`settings.game.${key}`) }}</div>
        <v-menu
          :model-value="openMenu === key"
          location="bottom start"
          :close-on-content-click="true"
          :disabled="disabled"
          @update:model-value="(open) => (openMenu = open ? key : null)"
        >
          <template #activator="{ props: activator }">
            <button
              v-bind="activator"
              type="button"
              class="color-swatch color-swatch--trigger"
              :style="{ backgroundColor: modelValue[key] ?? WHITE }"
              :aria-label="t(`settings.game.${key}`)"
              :disabled="disabled"
            ></button>
          </template>
          <v-card class="pa-3 color-picker">
            <div class="color-swatches" role="radiogroup" :aria-label="t(`settings.game.${key}`)">
              <button
                v-for="c in colors"
                :key="c"
                type="button"
                role="radio"
                class="color-swatch"
                :class="{ selected: modelValue[key] === c }"
                :style="{ backgroundColor: c }"
                :aria-label="c"
                :aria-checked="modelValue[key] === c"
                @click="update({ [key]: c })"
              ></button>
            </div>
            <!-- Any colour at all (color.rgb): a palette cannot list them, so the browser's own
                 picker stands in for it — on `change`, once the choice is done.
                 The clicks are stopped here on purpose: this menu closes on any click inside
                 itself, and closing it takes this input out of the page, which is what made
                 Chrome shut the picker it had just opened. -->
            <div v-if="anyColor" class="d-flex align-center mt-3" @mousedown.stop @click.stop>
              <input
                type="color"
                :value="modelValue[key] ?? WHITE"
                :aria-label="t('settings.game.freeColor')"
                @change="pickFree(key, $event)"
              />
              <span class="text-body-2 ml-2">{{ t('settings.game.freeColor') }}</span>
            </div>
          </v-card>
        </v-menu>
      </div>
    </div>

    <!-- a car with no liveries has nothing to pick: the overrides are still saved, and apply to
         whichever car does have them (a livery id a car does not list draws no overlay) -->
    <div v-if="liveryItems.length > 1" class="d-flex align-center mt-4">
      <div class="text-subtitle-2 color-picker-label">{{ t('settings.game.livery') }}</div>
      <v-select
        :model-value="modelValue.livery ?? ''"
        :items="liveryItems"
        variant="outlined"
        hide-details
        class="flex-grow-1"
        :disabled="disabled"
        @update:model-value="(value) => update({ livery: value || null })"
      ></v-select>
    </div>
  </div>
</template>

<style scoped>
/* The car, drawn on a neutral panel: the sprites are the game's own, so the colours around them
   say nothing about how they will look on a track. */
.car-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px;
  border-radius: 8px;
  background: #26282e;
}

/* The colours: plain swatches, one per colour this account may pick. Each cell shows only the one
   in use and opens the palette on click, so the paint stays a few lines tall however many colours
   the livery takes.
   Four labels and their swatches fit side by side, so the cells go two to a row; under Vuetify's
   small breakpoint there is no room for that and they stack. */
.color-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 24px;
}

@media (max-width: 599.98px) {
  .color-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.color-picker-label {
  width: 88px;
}

.color-picker {
  width: 316px;
}

.color-picker .color-swatch {
  width: 44px;
  height: 32px;
}

.color-swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.color-swatch {
  width: 60px;
  height: 44px;
  border-radius: 8px;
  border: 3px solid transparent;
  cursor: pointer;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.15);
  transition: border-color 0.15s, transform 0.15s;
}

.color-swatch:hover {
  transform: scale(1.08);
}

.color-swatch.selected {
  border-color: rgb(var(--v-theme-primary));
}

.color-swatch:disabled {
  cursor: default;
  opacity: 0.6;
}
</style>
